import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { AranModalService } from '@aran-client/aran-web-ui-toolkit';
import { tap } from 'rxjs';
import { routeMetaData } from 'src/app/shared/constants/route-metadata.constant';
import { NavigationService } from 'src/app/shared/services/navigation/navigation.service';
import { StoreHelperService } from 'src/app/shared/services/store-helper/store-helper.service';
import {
  ForgotPswResetType,
  ForgotPswSendCodeType,
  ForgotPswVerifyCodeType,
} from 'src/app/shared/types/auth.types';
import { ErrorMessages } from 'src/app/shared/utils/error-message.utils';
import { PasswordUtils } from 'src/app/shared/utils/password.utils';
import { AuthAction } from 'src/app/store/auth/auth.action';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
})
export class ForgotPasswordComponent implements OnInit {
  @ViewChild('modalTemplate') modalTemplate!: TemplateRef<any>;

  routeMetaData = routeMetaData;

  form!: FormGroup<any>;
  verified: boolean = false;
  errorMessages = ErrorMessages;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private aranModalService: AranModalService,
    private navigation: NavigationService,
    private storeHelperService: StoreHelperService
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      email: this.formBuilder.control(null, [
        Validators.required,
        Validators.email,
      ]),
      password: this.formBuilder.control(null),
      confirmPassword: this.formBuilder.control(null),
      code: this.formBuilder.control(null),
    });

    PasswordUtils.bindPasswordConfirmation(
      this.form,
      'password',
      'confirmPassword'
    );
  }

  onVerify() {
    this.verified = true;

    if (this.verified) {
      this.form
        .get('password')
        ?.setValidators([
          Validators.required,
          PasswordUtils.passwordValidator(),
        ]);
      this.form
        .get('confirmPassword')
        ?.setValidators([
          Validators.required,
          PasswordUtils.passwordCompareValidator('password'),
        ]);
      this.form.get('code')?.setValidators(Validators.required);
    } else {
      this.form.get('password')?.clearValidators();
      this.form.get('confirmPassword')?.clearValidators();
      this.form.get('code')?.clearValidators();
    }

    this.form.get('password')?.updateValueAndValidity();
    this.form.get('confirmPassword')?.updateValueAndValidity();
    this.form.get('code')?.updateValueAndValidity();
  }

  onEnterPressed(): void {
    this.reset();
  }

  reset(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.verified) {
      const formValue = this.form.value;

      delete formValue.confirmPassword;

      const body = this.form.value as ForgotPswResetType;

      this.storeHelperService
        .getAllDispatchAndSelect(new AuthAction.ForgotPswReset(body))
        .pipe(
          tap(() => {
            this.navigation.goToLogin();
          })
        )
        .subscribe();
    } else {
      const formValue = this.form.value;

      delete formValue.password;
      delete formValue.confirmPassword;
      delete formValue.code;

      const body = this.form.value as ForgotPswSendCodeType;

      this.storeHelperService
        .getAllDispatchAndSelect(new AuthAction.ForgotPswSendCode(body))
        .pipe(
          tap(() => {
            this.openDialog();
          })
        )
        .subscribe();
    }
  }

  forgotPswConfirm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.value;

    delete formValue.password;
    delete formValue.confirmPassword;

    var body = this.form.value as ForgotPswVerifyCodeType;

    this.storeHelperService
      .getAllDispatchAndSelect(new AuthAction.ForgotPswVerifyCode(body))
      .pipe(
        tap(() => {
          this.aranModalService.hide();
          this.onVerify();
        })
      )
      .subscribe();
  }

  openDialog() {
    this.aranModalService.show(this.modalTemplate, '600px');
  }

  get criteriaList() {
    const password = this.form.get('password')?.value || '';
    return PasswordUtils.getCriteria(password);
  }
}
